import { useCallback, useEffect, useState } from 'react'
import type { Coordinate } from '@/types'

export interface ContextMenuState {
	drawingId: string
	pointId: string
	pixelPosition: { x: number; y: number }
}

export interface AddPopupState {
	drawingId: string
	segmentIndex: number
	insertPosition: Coordinate
	pixelPosition: { x: number; y: number }
}

export interface UsePopupStateReturn {
	contextMenuState: ContextMenuState | null
	addPopupState: AddPopupState | null
	setContextMenuState: (state: ContextMenuState | null) => void
	setAddPopupState: (state: AddPopupState | null) => void
	closeAll: () => void
}

/**
 * Hook to manage popup state (context menu and add popup) with automatic click-outside handling.
 */
export function usePopupState(): UsePopupStateReturn {
	const [contextMenuState, setContextMenuState] = useState<ContextMenuState | null>(null)
	const [addPopupState, setAddPopupState] = useState<AddPopupState | null>(null)

	// Stable reference prevents unnecessary effect re-runs
	const closeAll = useCallback(() => {
		setContextMenuState(null)
		setAddPopupState(null)
	}, [])

	// Close popup on click outside
	useEffect(() => {
		if (!contextMenuState && !addPopupState) return

		function handleClickOutside(_e: MouseEvent) {
			closeAll()
		}

		// Use setTimeout to avoid immediately closing from the same event
		const timeout = setTimeout(() => {
			window.addEventListener('click', handleClickOutside)
			window.addEventListener('contextmenu', handleClickOutside)
		}, 0)

		return () => {
			clearTimeout(timeout)
			window.removeEventListener('click', handleClickOutside)
			window.removeEventListener('contextmenu', handleClickOutside)
		}
	}, [contextMenuState, addPopupState, closeAll])

	return {
		contextMenuState,
		addPopupState,
		setContextMenuState,
		setAddPopupState,
		closeAll,
	}
}
